import React from "react";
import ReactDOMServer from "react-dom/server";
import { addDays } from "date-fns";
import { FaTasks, FaGlobeAmericas } from "react-icons/fa";
import { Button } from "react-bootstrap";
import ReactDOM from "react-dom";
import ComovaGanttChart from "./ComovaGanttChart";
import type { ComovaNode } from "./types";
import type { Project } from "../../../graphql-client/codegen-types";
import { WbsNodeStatus } from "../../../graphql-client/codegen-types";
import { briefFormat, Rhombus } from "../../../components/utils";
import {
  getGanttPNGScreenshot,
  statusIcons,
} from "../../components/gantt/utils";
import Loader from "../../../components/Loader";

// Transforma JSX (React Components) en strings de html.
// Esto es útil porque dhtmlx usa html crudo para renderear.
// Documentación: https://reactjs.org/docs/react-dom-server.html
const jsxToStr = ReactDOMServer.renderToString;

class PrintableGanttChart extends ComovaGanttChart {
  setUpColumns() {
    super.setUpColumns();
    const getStatusLabel = (status: WbsNodeStatus) => {
      if (status === WbsNodeStatus.Pending) return "No iniciado";
      if (status === WbsNodeStatus.InProgress) return "En progreso";
      return "Terminado";
    };
    const getStatusIcon = (status: WbsNodeStatus) => {
      if (status === WbsNodeStatus.Pending) return statusIcons.pending;
      if (status === WbsNodeStatus.InProgress) return statusIcons.inProgress;
      return statusIcons.done;
    };
    const getIcon = ({ level }: ComovaNode) =>
      [
        <FaGlobeAmericas style={{ color: "#0086ba" }} />,
        <Rhombus />,
        <FaTasks style={{ color: "#999900" }} />,
      ][level];
    const textColumn = {
      name: "text",
      label: " ",
      width: "160",
      template: (task: ComovaNode) => {
        const charLimit = 20;
        const multilineAttributes =
          task.text.length > charLimit ? { lineHeight: "18px" } : {};
        const getMultilineText = () => {
          if (task.text.length <= charLimit) return task.text;
          return (
            <span>
              {" "}
              {task.text.substring(0, charLimit)} <br />{" "}
              {task.text.substring(charLimit)}{" "}
            </span>
          );
        };
        return jsxToStr(
          <div
            style={{
              fontSize: "14px",
              textAlign: "left",
              color: task.readonly ? "grey" : "inherit",
              ...multilineAttributes,
            }}
          >
            {getIcon(task)} {getMultilineText()}
          </div>
        );
      },
    };
    const statusColumn = {
      name: "status",
      label: "Estatus",
      width: "60",
      template: (node: ComovaNode) =>
        jsxToStr(
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "9px",
              margin: "3px",
            }}
          >
            {getStatusIcon(node.status)}
            <span
              className="text-muted"
              style={{ margin: "0", padding: "0", lineHeight: "12px" }}
            >
              {getStatusLabel(node.status)}
            </span>
          </div>
        ),
    };
    const startDateColumn = {
      name: "start",
      label: jsxToStr(
        <div style={{ margin: "5px", lineHeight: "19px" }}>
          Inicio <br />
          Real
        </div>
      ),
      width: "90",
      template: (task: ComovaNode) =>
        jsxToStr(
          <div
            style={{
              fontSize: "14px",
              color: task.readonly ? "grey" : "inherit",
            }}
          >
            {briefFormat(task.start_date)}
          </div>
        ),
    };
    const endDateColumn = {
      name: "end",
      label: jsxToStr(
        <div style={{ margin: "5px", lineHeight: "19px" }}>
          Terminación
          <br />
          Real
        </div>
      ),
      width: "90",
      template: (task: ComovaNode) =>
        jsxToStr(
          <div
            style={{
              fontSize: "14px",
              color: task.readonly ? "grey" : "inherit",
            }}
          >
            {briefFormat(addDays(task.end_date, -1))}
          </div>
        ),
    };

    this.myGantt.config.columns = [
      textColumn,
      statusColumn,
      startDateColumn,
      endDateColumn,
    ];
  }

  configureGantt() {
    super.configureGantt();
    this.myGantt.config.row_height = 40;
  }
}

type Props = {
  text: string;
  project: Project;
  onGanttExport: (png: any, updatedProject: Project) => Promise<void>;
  statusDate: Date;
};

const ComovaGanttPNGExporter = ({
  text,
  project,
  onGanttExport,
  statusDate,
}: Props) => {
  const ganttContainerRef = React.createRef<HTMLDivElement>();
  const hiddenElementRef = React.createRef<HTMLDivElement>();
  const [loading, setLoading] = React.useState(false);

  const onClick = () => {
    setLoading(true);
    if (!hiddenElementRef.current) throw Error("hidden element is undefined");
    const hiddenElement = hiddenElementRef.current;
    const onAfterRender = () => {
      setTimeout(() => {
        if (!ganttContainerRef.current) throw Error("gantt is undefined");
        getGanttPNGScreenshot(ganttContainerRef.current)
          .then((ganttPNG) => onGanttExport(ganttPNG, project))
          .then(() => {
            ReactDOM.unmountComponentAtNode(hiddenElement);
            setLoading(false);
          });
      }, 0);
    };
    const { wbsRoot, links } = project;
    if (!wbsRoot || !links) throw Error("wbs root or links is null");
    ReactDOM.render(
      <PrintableGanttChart
        ganttContainerRef={ganttContainerRef}
        statusDate={statusDate}
        wbsRoot={wbsRoot}
        links={links}
        onTaskUpdate={() => Promise.resolve()}
        proVerifier={[() => false, false]}
        onLinkAdd={() => Promise.resolve()}
        onLinkDelete={() => Promise.resolve()}
        readOnly
      />,
      hiddenElement,
      onAfterRender
    );
  };

  return (
    <div>
      {loading ? (
        <Loader size={60} message="Imprimiendo..." />
      ) : (
        <Button onClick={onClick} variant="info">
          {" "}
          {text}{" "}
        </Button>
      )}
      <div
        ref={hiddenElementRef}
        style={{
          position: "absolute",
          top: "0",
          left: "-10000px",
          width: "800px",
        }}
      />
    </div>
  );
};

export default ComovaGanttPNGExporter;
